// Aircraft autopilot (AVI-3): a native, deterministic PID altitude-hold +
// heading-hold flying the M32 aircraft through the same control verbs a pilot uses
// (elevator = pitch, aileron = roll, throttle). PURE (ENG-1): no three/DOM/random,
// so it runs in the sim tick, replays under REP (M33), and is unit-testable against
// step-response goldens. The controller shape — a PID with an integral clamp and
// output saturation, derivative on the error, "the integral is the aircraft's
// scale" — is ported from the CTWG `flight-control-systems` lesson 10 (M35 will
// expose the gains as an editable Script-Block preset; no hard dep, built natively
// here). Gains differ from the lesson's drone (a different plant): the aircraft is
// a fixed-wing whose altitude is flown with the elevator, tuned once in decisions.md.
import { AVI } from '../config';
import type { Aircraft, AircraftInput } from './aircraft';

export interface PidGains { kp: number; ki: number; kd: number; }
export interface PidLimits { outMin: number; outMax: number; iMin: number; iMax: number; }

/** A discrete PID with anti-windup integral clamp + output saturation (lesson 10). */
export interface Pid {
  /** Advance one step with the current error and dt (seconds); returns the clamped output. */
  step(error: number, dt: number): number;
  /** The integral term `ki·∫e` — in a settled hold this converges to the plant's bias
   *  ("the integral is the aircraft's scale"). */
  readonly iTerm: number;
  reset(): void;
}

const clamp = (x: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, x));

/** Build a PID controller (faithful port of the lesson's `makePid`). */
export function makePid(gains: PidGains, limits: PidLimits): Pid {
  let integral = 0;
  let prevError: number | null = null;
  return {
    step(e, dt) {
      integral = clamp(integral + e * dt, limits.iMin, limits.iMax); // anti-windup clamp
      const d = prevError === null ? 0 : (e - prevError) / dt;
      prevError = e;
      return clamp(gains.kp * e + gains.ki * integral + gains.kd * d, limits.outMin, limits.outMax);
    },
    get iTerm() { return gains.ki * integral; },
    reset() { integral = 0; prevError = null; },
  };
}

/** Shortest signed difference a−b of two headings (radians), in (−π, π]. */
export function angleDiff(a: number, b: number): number {
  return Math.atan2(Math.sin(a - b), Math.cos(a - b));
}

/** Autopilot targets. A null heading holds wings level (altitude-hold only). */
export interface AutopilotTargets {
  altitude: number;
  heading: number | null; // radians, player yaw frame; null → wings level
  throttle: number;       // 0..1 cruise throttle to hold
}

// ── Gains (tuned once for the fixed-wing elevator/aileron plant, decisions.md M32).
//    Cascade: an outer PID on the tracked quantity (altitude / heading) commands an
//    attitude (pitch / roll), and a proportional inner loop drives the control
//    surface to that attitude — the aircraft integrates surface deflection into
//    attitude, so the inner P closes the extra integrator the drone plant lacked.
//    Altitude adds a TRIM_PITCH feedforward: a fixed-wing holds level at a non-zero
//    nose-up attitude (unlike the drone, whose hover is pure integral), so the PID
//    only has to trim residual droop → the lesson's "small Ki" (windup-safe) works.
const TRIM_PITCH = 0.13;                                                  // level-flight pitch (rad)
const ALT = { gains: { kp: 0.030, ki: 0.004, kd: 0.040 }, out: 0.20, iMax: 0.10 }; // → Δpitch (rad)
const HDG = { gains: { kp: 0.50, ki: 0.0, kd: 0.50 }, iMax: 0.5 };        // → target roll (rad)
const INNER_PITCH = 8;  // elevator per radian of pitch error
const INNER_ROLL = 6;   // aileron per radian of roll error
const DT = 1 / 20;      // one sim tick, seconds (TPS=20)

/** PID altitude-hold + heading-hold for the M32 aircraft (AVI-3). */
export class Autopilot {
  private readonly altPid: Pid;
  private readonly hdgPid: Pid;

  /** `altGains` overrides the tuned altitude PID — this is the seam the M35
   *  Script-Block preset uses to expose Kp/Ki/Kd. It defaults to the tuned
   *  fixed-wing gains, so the native autopilot (M32) is bitwise-unchanged. */
  constructor(altGains: PidGains = ALT.gains) {
    this.altPid = makePid(altGains, { outMin: -ALT.out, outMax: ALT.out, iMin: -ALT.iMax, iMax: ALT.iMax });
    this.hdgPid = makePid(HDG.gains, { outMin: -AVI.ROLL_MAX, outMax: AVI.ROLL_MAX, iMin: -HDG.iMax, iMax: HDG.iMax });
  }

  reset(): void { this.altPid.reset(); this.hdgPid.reset(); }

  /** The integral of the altitude loop — the residual pitch trim the aircraft settles
   *  at ("the integral is the aircraft's scale", lesson 10). */
  get altITerm(): number { return this.altPid.iTerm; }

  /** Compute this tick's controls to fly toward the targets (AVI-3). */
  step(a: Aircraft, t: AutopilotTargets): AircraftInput {
    // Altitude → target pitch attitude (trim + PID) → elevator (inner P closes on pitch).
    const targetPitch = TRIM_PITCH + this.altPid.step(t.altitude - a.y, DT);
    const elevator = clamp(INNER_PITCH * (targetPitch - a.pitch), -1, 1);
    // Heading → target roll (bank) → aileron; null heading holds wings level. The
    // error is yaw−target because a right bank turns the nose toward lower yaw.
    const targetRoll = t.heading === null ? 0 : this.hdgPid.step(angleDiff(a.yaw, t.heading), DT);
    const aileron = clamp(INNER_ROLL * (targetRoll - a.roll), -1, 1);
    // Throttle: bang-bang toward the cruise setting (the plant's only throttle verbs).
    return {
      pitch: elevator,
      roll: aileron,
      throttleUp: a.throttle < t.throttle - AVI.THROTTLE_RATE / 2,
      throttleDown: a.throttle > t.throttle + AVI.THROTTLE_RATE / 2,
    };
  }
}

// ── M35 Script-Block preset (SCR-4, wraps AVI-3) ─────────────────────────────
/** The editable autopilot preset a `script_block` stores: a target altitude/heading
 *  plus the tunable altitude PID gains. Persisted with the block; the tuned heading
 *  loop and inner surfaces stay native. */
export interface AutopilotPreset {
  altitude: number;
  /** radians (player yaw frame); null → hold wings level. */
  heading: number | null;
  kp: number;
  ki: number;
  kd: number;
}

/** The default preset (target 100 m, wings level, the M32-tuned altitude gains). */
export const DEFAULT_AUTOPILOT_PRESET: AutopilotPreset = {
  altitude: 100,
  heading: null,
  kp: ALT.gains.kp,
  ki: ALT.gains.ki,
  kd: ALT.gains.kd,
};

/** Cruise throttle the preset holds while flying (the M32 trim setting). */
export const PRESET_CRUISE_THROTTLE = 0.5;

/** Build the autopilot + targets a preset engages — an `Autopilot` with the preset's
 *  editable gains plus the altitude/heading it should track. */
export function autopilotFromPreset(p: AutopilotPreset): { autopilot: Autopilot; targets: AutopilotTargets } {
  return {
    autopilot: new Autopilot({ kp: p.kp, ki: p.ki, kd: p.kd }),
    targets: { altitude: p.altitude, heading: p.heading, throttle: PRESET_CRUISE_THROTTLE },
  };
}
